import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useSiteData } from '../contexts/SiteDataContext';
import { publicAPI } from '../services/api';
import { formatWhatsAppLink, getCategoryQuoteMessage, getDefaultQuoteMessage } from '../utils/whatsapp';
import { resolveImageUrl } from '../utils/imageUrl';

const CategoryDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { categories } = useSiteData();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = categories.find(c => c.slug === slug);

  useEffect(() => {
    if (category) {
      publicAPI.getProducts({ category: category.name })
        .then(setProducts)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [category]);

  if (!category) return (
    <div className="min-h-screen bg-gray-50"><Header />
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h2 className="text-3xl font-bold text-gray-600 mb-4">Categoria não encontrada</h2>
        <Button onClick={() => navigate('/categorias')} className="bg-[#055509]">Voltar para Categorias</Button>
      </div><Footer /></div>
  );

  const handleQuoteClick = (e, product) => {
    e.stopPropagation();
    window.open(formatWhatsAppLink(getDefaultQuoteMessage(product.code, product.name)), '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/categorias')} className="flex items-center text-[#055509] hover:text-[#FFB025] transition-colors">
            <ArrowLeft size={20} className="mr-2" />Voltar para Categorias
          </button>
        </div>
      </div>

      <section className="bg-gradient-to-r from-[#055509] to-[#0a7a0f] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{category.name}</h1>
          <p className="text-xl text-gray-200 mb-6">{category.description}</p>
          <div className="text-lg"><span className="text-[#FFB025] font-semibold">{category.productCount}</span> produtos disponíveis</div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
          ) : products.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((product) => (
                <Card key={product.id} className="group cursor-pointer hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-[#FFB025] overflow-hidden" onClick={() => navigate(`/produto/${product.id}`)}>
                  <div className="relative h-64 overflow-hidden bg-gray-100">
                    <img src={resolveImageUrl(product.mainImage || product.images?.[0])} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjQwMCIgaGVpZ2h0PSI0MDAiIGZpbGw9IiNFNUU3RUIiLz48dGV4dCB4PSIyMDAiIHk9IjIwMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZpbGw9IiM5Q0EzQUYiPlNlbSBJbWFnZW08L3RleHQ+PC9zdmc+'; }} />
                    <div className="absolute top-4 left-4"><Badge className="bg-[#055509] text-white">{product.category}</Badge></div>
                  </div>
                  <CardContent className="p-6">
                    <div className="text-sm font-mono text-[#FFB025] font-semibold mb-2">{product.code}</div>
                    <h3 className="text-xl font-bold text-[#055509] mb-3 group-hover:text-[#FFB025] transition-colors line-clamp-2">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.shortDescription}</p>
                    <div className="mb-4">
                      <div className="text-xs font-semibold text-gray-500 mb-2">APLICAÇÕES:</div>
                      <div className="flex flex-wrap gap-2">
                        {(product.applications || []).slice(0, 2).map((app, idx) => <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">{app}</span>)}
                        {(product.applications || []).length > 2 && <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">+{product.applications.length - 2}</span>}
                      </div>
                    </div>
                    <div className="flex gap-2 pt-4 border-t border-gray-100">
                      <Button size="sm" onClick={(e) => handleQuoteClick(e, product)} className="flex-1 bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white font-semibold">
                        <MessageCircle size={16} className="mr-2" />Cotação
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <h3 className="text-2xl font-bold text-gray-600 mb-4">Produtos desta categoria em breve</h3>
              <p className="text-gray-500 mb-6">Entre em contato para consultar disponibilidade</p>
              <Button onClick={() => window.open(formatWhatsAppLink(getCategoryQuoteMessage(category.name)), '_blank')} className="bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white">
                <MessageCircle className="mr-2" size={20} />Consultar Especialista
              </Button>
            </div>
          )}
        </div>
      </section>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CategoryDetail;
